import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	AppointmentIdentifier,
	cancelAppointment,
	CancelCalendarAppointmentModel,
	RecurringAppointmentOperationKind
} from "client/Api/Calendar/calendar";
import {
	Dialog,
	Form,
	FormItem,
	TextArea,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation

} from "client/Hooks";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	isNil,
	isNotNil,
	resolveFormattedTextString
} from "client/Misc/utils";

export type CancelAppointmentDialogProps = {
	onClose: (wasCancelled?: boolean) => void;
	appointmentIdentifier: AppointmentIdentifier;
	recurringOperationKind?: RecurringAppointmentOperationKind;
};

export const CancelAppointmentDialog = (props: CancelAppointmentDialogProps): ReactElement => {
	const {
		onClose,
		appointmentIdentifier,
		recurringOperationKind,
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({ comment: "" });

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;
			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await cancelAppointment(
					{
						identifier: appointmentIdentifier,
						comment: formData.comment,
						recurringOperationKind: isNil(recurringOperationKind)
							? { isSet: false }
							: { isSet: true, value: recurringOperationKind }
					},
					globalCancellation
				);

				onClose(true);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			appointmentIdentifier,
			formData.comment,
			formHandler,
			globalCancellation,
			isBusy,
			onClose,
			recurringOperationKind
		]
	);

	const messageFieldLabel = useMemo(
		(): string => resolveFormattedTextString({
			template: localize("_OptionalLabelFormat"),
			replacements: [{ what: "{{Label}}", filler: localize("_AddMessage") }]
		}),
		[]
	);

	return (
		<Dialog
			onClose={onClose}
			hasBusyIndicator={isBusy}
			trackingId="cancel-appointment-dialog"
		>
			<Dialog.Header
				title={getAppointmentCancelMessage(recurringOperationKind)}
			/>
			<Dialog.Body>
				<Form
					onSubmit={submitHandler}
				>
					<FormItem
						label={messageFieldLabel}
					>
						<TextArea
							{...formHandler.for("comment").attachToTextArea()}
							placeholder={localize("_EnterMessage")}
							rows={3}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Send")}
						onClick={submitHandler}
						trackingId="send"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
CancelAppointmentDialog.displayName = "CancelAppointmentDialog";

type FormData = Omit<CancelCalendarAppointmentModel, "recurringOperationKind" | "identifier">;

const operationKindToCancelMessageMap: {
	[key in RecurringAppointmentOperationKind]: LocalizationKey;
} = {
	[RecurringAppointmentOperationKind.All]: "_CancelAllEventsInTheSeriesAndNotifyAttendees",
	[RecurringAppointmentOperationKind.CurrentAndFollowing]: "_CancelThisAndAllFollowingEventsInTheSeriesAndNotifyAttendees",
	[RecurringAppointmentOperationKind.Current]: "_CancelThisEventAndNotifyAttendees"
};

export function getAppointmentCancelMessage(
	recurringOperationKind: RecurringAppointmentOperationKind | undefined
): string {
	return localize(
		isNotNil(recurringOperationKind)
			? operationKindToCancelMessageMap[recurringOperationKind]
			: "_CancelThisEventAndNotifyAttendees"
	);
}